import React from 'react';
import { Link, usePage } from '@inertiajs/react';
import {
  LayoutDashboard,
  Users,
  UserCheck,
  FileText,
  Clock,
  Eye,
  ArrowLeft,
  LogOut,
  Filter,
  UserPlus
} from 'lucide-react';

interface UserProps {
  id: number;
  email_address: string;
}

interface PageProps {
  auth?: {
    user?: UserProps;
  };
  [key: string]: any;
}

interface AdminLayoutProps {
  children: React.ReactNode;
  title?: string;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, title = 'Administration' }) => {
  const { auth } = usePage<PageProps>().props;
  const currentUser = auth?.user;

  const navigation = [
    {
      name: 'Dashboard',
      href: '/admin',
      icon: LayoutDashboard,
      current: window.location.pathname === '/admin'
    },
    {
      name: 'Utilisateurs',
      href: '/admin/users',
      icon: Users,
      current: window.location.pathname.startsWith('/admin/users')
    },
    {
      name: 'Kinésithérapeutes',
      href: '/admin/physiotherapists',
      icon: UserCheck,
      current: window.location.pathname.startsWith('/admin/physiotherapists')
    },
    {
      name: 'Articles',
      href: '/admin/blog_posts',
      icon: FileText,
      current: window.location.pathname.startsWith('/admin/blog_posts')
    },
    {
      name: 'Sessions',
      href: '/admin/sessions',
      icon: Clock,
      current: window.location.pathname.startsWith('/admin/sessions')
    },
    {
      name: 'Page Views',
      href: '/admin/page_views',
      icon: Eye,
      current: window.location.pathname.startsWith('/admin/page_views')
    },
    {
      name: 'Filtres',
      href: '/admin/filter_categories',
      icon: Filter,
      current: window.location.pathname.startsWith('/admin/filter_categories')
    },
    {
      name: 'Questionnaires',
      href: '/admin/questionnaire_requests',
      icon: FileText,
      current: window.location.pathname.startsWith('/admin/questionnaire_requests')
    },
    {
      name: 'Revendications',
      href: '/admin/claim_requests',
      icon: UserPlus,
      current: window.location.pathname.startsWith('/admin/claim_requests')
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Sidebar */}
        <div className="bg-gray-900 text-white w-64 min-h-screen p-4">
          <div className="mb-8">
            <h1 className="text-xl font-bold text-green-400">Ethos Santé</h1>
            <p className="text-gray-400 text-sm">Administration</p>
          </div>
          
          <nav className="space-y-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                className={`flex items-center px-3 py-2 text-gray-300 hover:bg-gray-800 hover:text-white rounded-md transition-colors ${
                  item.current ? 'bg-gray-800 text-white' : ''
                }`}
              >
                <item.icon className="w-5 h-5 mr-3" />
                {item.name}
              </Link>
            ))}
          </nav>
          
          <div className="mt-8 pt-8 border-t border-gray-700">
            <Link 
              href="/" 
              className="flex items-center px-3 py-2 text-gray-300 hover:bg-gray-800 hover:text-white rounded-md transition-colors"
            >
              <ArrowLeft className="w-5 h-5 mr-3" />
              Retour au site
            </Link>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1">
          {/* Header */}
          <header className="bg-white shadow-sm border-b border-gray-200">
            <div className="px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-semibold text-gray-900">
                  {title}
                </h2>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-500">
                    Connecté en tant que {currentUser?.email_address}
                  </span>
                  <Link 
                    href="/session" 
                    method="delete" 
                    as="button" 
                    className="text-red-600 hover:text-red-800 text-sm flex items-center"
                  >
                    <LogOut className="w-4 h-4 mr-1" />
                    Déconnexion
                  </Link>
                </div>
              </div>
            </div>
          </header>

          {/* Page content */}
          <main className="p-6">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout; 
